import { Marker as ClusterMarker } from "@googlemaps/markerclusterer";
import { useMap } from "@vis.gl/react-google-maps";
import { memo, useEffect, useRef, useState } from "react";
import { config } from "../../config";
import { useSelector } from "react-redux";

interface MarkerProps {
  listing: any;
  type: string;
  setMarkerRef: (marker: ClusterMarker | null, key: string) => void;
  onClick: (itm: any, e: any) => void;
  selected: boolean;
  clusterer?: any;
}

function getZillowPin(data: any) {
  let basePath = "images/markers";
  return config.assetsUrl + `${basePath}/zillow/${data.price_drop}.png`;
}

function getMarkerPin(
  public_id: string,
  data: any[],
  selected = false,
  hovered = false
) {
  const obj = data.find((item) => item.public_id === public_id);

  let basePath = "images/markers";
  let path = basePath;
  if (hovered) {
    path = `${basePath}/hovered`;
  }
  if (selected) {
    path = `${basePath}/selected`;
  }

  if (obj) {
    return (
      config.assetsUrl + `${path}/${obj.option_name.replace(/\s+/g, "_")}.png`
    );
  } else {
    return config.assetsUrl + `${path}/Working.png`;
  }
}

const MarkerComponent = ({
  listing,
  onClick,
  setMarkerRef,
  selected,
  type,
  clusterer,
}: MarkerProps) => {
  const markerRef = useRef<google.maps.Marker | null>(null);
  const [instance, setInstance] = useState<google.maps.Marker | null>(null);
  const [clickListener, setClickListener] =
    useState<google.maps.MapsEventListener | null>(null);
  const [mouseoutListener, setMouseoutListener] =
    useState<google.maps.MapsEventListener | null>(null);
  const [mouseoverListener, setMouseoverListener] =
    useState<google.maps.MapsEventListener | null>(null);
  const [hovered, setHovered] = useState(false);
  const leadStatusOptions = useSelector(
    (state: any) => state.listingReducer.leadStatusOptions
  );
  const hoveredListing = useSelector(
    (state: any) => state.listingReducer.hoveredListing
  );
  const map = useMap();
  useEffect(() => {
    if (instance !== null) {
      instance.setMap(map);
    }
  }, [map]);

  useEffect(() => {
    if (instance && listing.source_name !== "zillow") {
      const markerPin = getMarkerPin(
        listing.lead_status_options_public_id,
        leadStatusOptions,
        selected,
        hovered ||
          hoveredListing?.listing_unique_id === listing.listing_unique_id
      );
      instance.setIcon(markerPin);
      setMarkerRef(instance, listing.listing_unique_id);
    }
  }, [selected, instance, hovered, hoveredListing, listing]);

  useEffect(() => {
    const marker = new google.maps.Marker({
      position: {
        lat: parseFloat(listing?.latitude || ""),
        lng: parseFloat(listing?.longitude || ""),
      },
      icon: getMarkerPin(
        listing.lead_status_options_public_id,
        leadStatusOptions,
        selected
      ),
    });

    marker.setIcon(
      listing.source_name === "zillow"
        ? getZillowPin(listing)
        : getMarkerPin(
            listing.lead_status_options_public_id,
            leadStatusOptions,
            selected || hovered
          )
    );

    marker.setMap(map);

    setClickListener(
      google.maps.event.addListener(marker, "click", function (e: any) {
        onClick(listing, e);
      })
    );

    setMouseoverListener(
      google.maps.event.addListener(marker, "mouseover", function () {
        setHovered(true);
      })
    );

    setMouseoutListener(
      google.maps.event.addListener(marker, "mouseout", function () {
        setHovered(false);
      })
    );

    setInstance(marker);
    setMarkerRef(marker, listing.listing_unique_id);
    markerRef.current = marker;

    return () => {
      if (clickListener) {
        google.maps.event.removeListener(clickListener);
      }
      if (mouseoverListener) {
        google.maps.event.removeListener(mouseoverListener);
      }
      if (mouseoutListener) {
        google.maps.event.removeListener(mouseoutListener);
      }
      marker.setMap(null);
    };
  }, []);

  return null;
};

export const Marker = MarkerComponent;
